import { FuseAnimateGroup } from '@fuse'
import { Typography, CircularProgress } from '@material-ui/core'
import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import Loader from 'app/components/Loader'
import MainLayout from 'app/components/MainLayout'
import NotificationCard, {
  INITIAL_NOTIFICATION,
} from 'app/components/NotificationCard'
import Panel from 'app/components/Panel'
import EditInitialNotificationDialog from './EditInitialNotificationDialog'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import * as R from 'ramda'
import ErrorFetchingLoading from 'app/main/messages/notificationTimeline/ErrorFetchingLoading'
import ErrorView from 'app/components/ErrorView'
import ErrorBoundary from 'app/components/ErrorBoundary'
import LargeToolbar from 'app/components/LargeToolbar'
import ContentToolbarPage from 'app/components/ContentToolbarPage'
import { useInitialNotifcation } from './useInitialNotifcation'
import NetworkError from 'app/components/NetworkError'
import { useIsOnline } from 'commons/useIsOnline'
import { useAppName } from 'commons/usePartner'
import AccessControl from 'app/auth/AccessControl'
import { AccessType } from 'app/auth/accessControlService'

const InitialNotification = () => {
  const defaultLanguage = useDefaultLanguage()
  const appName = useAppName()

  const {
    isLoading: isNotificationLoading,
    succesValue: notification,
    fetch: fetchInitialNotification,
    error,
  } = useInitialNotifcation()

  const [isOnline] = useIsOnline()

  const [dailogState, setDialogState] = useState({
    isOpen: false,
  })

  const handleClickEdit = () => {
    setDialogState({ isOpen: true })
  }

  const hasInitialNotification = R.compose(R.not, R.isNil, R.prop('id'))

  if (!isOnline) {
    return <NetworkError onRetryFetching={() => fetchInitialNotification()} />
  }

  return (
    <AccessControl
      accesses={[
        AccessType.NOTIFICATION_SEND,
        AccessType.NOTIFICATION_UNSENT_OWNED,
        AccessType.ADMINISTRATION,
      ]}
    >
      <ContentToolbarPage
        contentToolbar={
          <LargeToolbar
            content={
              <>
                <Typography variant="h6">
                  <FormattedMessage id="initialNotification.header.title" />
                </Typography>
              </>
            }
          />
        }
        content={
          <MainLayout
            main={
              error ? (
                <ErrorFetchingLoading isShow error={error} />
              ) : (
                <>
                  <ErrorBoundary fallBack={<ErrorView />}>
                    <div className="flex flex-col">
                      <div className="w-full justify-center flex pt-0">
                        <Loader
                          isLoading={isNotificationLoading}
                          placeholder={
                            <FuseAnimateGroup
                              enter={{ animation: 'fadeIn' }}
                              leave={{ animation: 'fadeOut' }}
                            >
                              <div className="w-full h-full">
                                <CircularProgress
                                  className="mt-64"
                                  color="secondary"
                                />
                              </div>
                            </FuseAnimateGroup>
                          }
                        >
                          {() => {
                            if (hasInitialNotification(notification)) {
                              return (
                                <>
                                  <NotificationCard
                                    key={1}
                                    notification={notification}
                                    notificationType={INITIAL_NOTIFICATION}
                                    onEditInitialNotification={handleClickEdit}
                                    className="w-full mx-4"
                                  />
                                  <EditInitialNotificationDialog
                                    notification={notification}
                                    defaultLanguage={defaultLanguage}
                                    openState={dailogState}
                                    onClose={() => {
                                      setDialogState({ isOpen: false })
                                    }}
                                    onUpdateSuccess={() => {
                                      fetchInitialNotification()
                                    }}
                                  />
                                </>
                              )
                            } else {
                              return <ErrorView />
                            }
                          }}
                        </Loader>
                      </div>
                    </div>
                  </ErrorBoundary>
                </>
              )
            }
            side={
              <div className="ltr:ml-5 rtl:mr-5">
                <Panel
                  startText={
                    <FormattedMessage
                      id="initialNotification.sidePanel.title"
                      values={{
                        b: (text) => <b>{text}</b>,
                      }}
                    />
                  }
                  children={
                    <div className="py-8">
                      <Typography className="pb-16">
                        <FormattedMessage id="initialNotification.sidePanel.content.paragraph1" />
                      </Typography>
                      <Typography className="pb-16">
                        <FormattedMessage
                          id="initialNotification.sidePanel.content.paragraph2"
                          values={{ appName }}
                        />
                      </Typography>
                    </div>
                  }
                />
              </div>
            }
          />
        }
      />
    </AccessControl>
  )
}

export default InitialNotification
